import { useEffect, useState } from 'react';
import { profileImageService } from '@/services';

/**
 * Resolves a profile-image storage path to a signed URL with in-memory
 * batching + caching. Multiple components calling this hook on the same
 * tick coalesce into a single `profile-image-signed-url` edge function
 * call, so <Avatar>s in a list don't each fire their own request.
 *
 * Returns undefined while loading (so callers can render the emoji
 * fallback in the meantime) and null if the path is forbidden/missing.
 */
export function useAvatarImageUrl(path: string | null | undefined): {
  url: string | null | undefined;
  loading: boolean;
} {
  const [url, setUrl] = useState<string | null | undefined>(() => {
    if (!path) return null;
    const cached = cache.get(path);
    if (cached && cached.expiresAt > Date.now()) return cached.url;
    return undefined;
  });

  useEffect(() => {
    if (!path) {
      setUrl(null);
      return;
    }

    const cached = cache.get(path);
    if (cached && cached.expiresAt > Date.now()) {
      setUrl(cached.url);
      return;
    }

    let cancelled = false;
    const subscriber = (next: string | null) => {
      if (!cancelled) setUrl(next);
    };
    subscribe(path, subscriber);

    return () => {
      cancelled = true;
      unsubscribe(path, subscriber);
    };
  }, [path]);

  return { url, loading: path != null && url === undefined };
}

// ────────────────────────────────────────────────────────────────────────────
// Module-level batching / caching — one-per-tab state.
// ────────────────────────────────────────────────────────────────────────────

interface CacheEntry {
  url: string | null;
  expiresAt: number;
}

type Subscriber = (url: string | null) => void;

const cache = new Map<string, CacheEntry>();
const subscribers = new Map<string, Set<Subscriber>>();
const pendingPaths = new Set<string>();
let flushScheduled = false;

function subscribe(path: string, cb: Subscriber) {
  let set = subscribers.get(path);
  if (!set) {
    set = new Set();
    subscribers.set(path, set);
  }
  set.add(cb);
  pendingPaths.add(path);
  scheduleFlush();
}

function unsubscribe(path: string, cb: Subscriber) {
  const set = subscribers.get(path);
  if (!set) return;
  set.delete(cb);
  if (set.size === 0) subscribers.delete(path);
}

function scheduleFlush() {
  if (flushScheduled) return;
  flushScheduled = true;
  queueMicrotask(flush);
}

async function flush() {
  flushScheduled = false;
  const now = Date.now();

  // Only request paths that aren't already cached + valid.
  const pathsToFetch = Array.from(pendingPaths).filter((p) => {
    const cached = cache.get(p);
    return !cached || cached.expiresAt <= now;
  });
  pendingPaths.clear();

  if (pathsToFetch.length === 0) return;

  try {
    const { urls, expires_in } =
      await profileImageService.getSignedAvatarUrls(pathsToFetch);
    const expiresAt = Date.now() + expires_in * 1000 - 30_000; // refresh 30s before server expiry

    for (const p of pathsToFetch) {
      const u = urls[p] ?? null;
      cache.set(p, { url: u, expiresAt });
      const set = subscribers.get(p);
      if (set) set.forEach((cb) => cb(u));
    }
  } catch (err) {
    console.warn('[useAvatarImageUrl] sign batch failed:', err);
    // Notify subscribers so they can fall back to the emoji.
    for (const p of pathsToFetch) {
      const set = subscribers.get(p);
      if (set) set.forEach((cb) => cb(null));
    }
  }
}

/** Clears the module cache. Exposed for tests and post-logout cleanup. */
export function clearAvatarImageUrlCache() {
  cache.clear();
  subscribers.clear();
  pendingPaths.clear();
}
